import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { T_CustomerLobDetailObj } from "../../Feature_Login/Login_Types";
import { T_ApiResponse } from "../../../globalTypes/GlobalTypes";
import { MfLobCode } from "../../../utils/constants";
import { useCallback, useEffect, useReducer } from "react";
import {
  useLazyFetchFolioSchemeSummaryQuery,
  useLazyFetchFoliosSummaryQuery,
  useLazyValidatePanQuery,
} from "../MutualFund_API";
import { isEmpty } from "lodash";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { AxiosError } from "axios";
import {
  MF_HandlingReducerAction,
  MF_ReduxInitialState,
} from "./MF_ReduxActionHandler";
import {
  setFolioDetails,
  setFolioSchemeDetails,
  setMfDashboardDetails,
} from "../MutualFund_Slice";
import {
  T_MFDashboardDetails,
  T_ValidatePan_Request,
} from "../MutualFund_Types";
import { T_LocalMfBaseData } from "./MF_BaseData_Types";
export const UseMfBaseDataLoader = (
  panNumber: string,
  dob: string,
  loadFor: "secondary" | "primary" = "primary"
): T_LocalMfBaseData => {
  const [localState, updateLocalState] = useReducer(
    MF_HandlingReducerAction,
    MF_ReduxInitialState
  );
  const dispatch = useAppDispatch();
  const csAppSlice = useAppSelector((st) => st.CsAppSlice);
  const folioDetails = useAppSelector((st) => st.MutualFundSlice.folioDetails);
  const folioSchemeDetails = useAppSelector(
    (st) => st.MutualFundSlice.folioSchemeDetails
  );
  const mfDashboardDetail = useAppSelector(
    (st) => st.MutualFundSlice.mfDashboardDetails
  );
  const [
    fetchValidatePanApi,
    { error: errInValidatePan, isFetching: isLoadingValidatePan },
  ] = useLazyValidatePanQuery();
  const [
    fetchFolioSchemeSummaryDetails,
    { error: errInFolioSchemeSummary, isFetching: isLoadingSchemeSummary },
  ] = useLazyFetchFolioSchemeSummaryQuery();
  const [
    fetchFolioSummaryDetails,
    { error: errInFolioSummaryDetails, isFetching: isLoadingSummaryDetails },
  ] = useLazyFetchFoliosSummaryQuery();

  const userDetails = csAppSlice.userDetails;
  const customerAndLobDetails = userDetails?.customerAndLobDetails;

  const UserMFLobId = customerAndLobDetails?.filter(
    (item: T_CustomerLobDetailObj) =>
      item.LOB === MfLobCode && item.PRIMARY_SECONDARY_USER === loadFor
  );

  const checkAndinitApiFlow = useCallback(() => {
    /** FN for Loading LI Dashboard Details */
    if (isEmpty(UserMFLobId)) {
      updateLocalState({
        type: "error",
        payload: Error("MF lob id not present"),
      });
    } else if (isEmpty(folioDetails) && !errInValidatePan) {
      updateLocalState({ type: "fetching", payload: "" });
      api_ValidatePan();
    } else if (isEmpty(folioSchemeDetails) && !errInFolioSchemeSummary) {
      updateLocalState({ type: "fetching", payload: "" });
      api_fetchFolioSchemeSummaryDetails();
    } else {
      updateLocalState({ type: "success", payload: "" });
      if (!mfDashboardDetail.XIRR_VALUE && !errInFolioSummaryDetails) {
        api_fetchFolioSummaryDetails();
      }
    }
  }, [
    UserMFLobId,
    panNumber,
    dob,
    folioDetails,
    folioSchemeDetails,
    mfDashboardDetail,
  ]);

  const api_ValidatePan = useCallback(async () => {
    let apiBody: T_ValidatePan_Request = {
      PANNo: panNumber,
      AadharNo: null,
      DOB: dob,
      ClientIpAddress: null,
      IMEI: null,
      OS: null,
      IsNew: null,
      isMinor: null,
      Channel: null,
      PageURL: null,
      Campaign: null,
      Trans_No: null,
      TransactionType: null,
      ServerIpAddress: null,
      ModuleName: null,
      MethodName: null,
      AuthCode: null,
      GoogleAdID: null,
      DeviceId: null,
      UserName: null,
      isFolioRequired: true,
      IS_PRIMARY_HOLDING: true
    };
    await fetchValidatePanApi(apiBody, false).then((res) => {
      if (isLoadingValidatePan) {
        return;
      }
      if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        let folioList = res?.data?.payload.Folios || [];
        if (isEmpty(folioList)) {
          updateLocalState({ type: "error", payload: Error("Empty List") });
        } else {
          dispatch(setFolioDetails(folioList));
        }
      } else {
        dispatch({ type: "Error", payload: res.error || Error("api failed") });
      }
    });
  }, [panNumber, dob, isLoadingValidatePan]);

  const api_fetchFolioSchemeSummaryDetails = useCallback(() => {
    if (isLoadingSchemeSummary) {
      return;
    }
    let folioString = folioDetails.map((item) => item.Folio_No).join(",");
    const apiBody_schemeSummary = {
      P_FOLIO_NO: folioString,
      P_PAN_NO: panNumber || "",
      P_IS_MINOR: "",
      IS_PRIMARY_HOLDING: loadFor === "primary" ? true : false,
    };
    fetchFolioSchemeSummaryDetails(apiBody_schemeSummary).then(
      async (resData) => {
        if (resData.data?.code === API_STATUS_CODE.SUCCESS) {
          if (isEmpty(resData.data?.payload.REF_CUR)) {
            updateLocalState({ type: "error", payload: Error("Empty List") });
          } else {
            const schemeDetails = resData.data?.payload.REF_CUR;
            dispatch(setFolioSchemeDetails(schemeDetails));
            let dashboardDetails: T_MFDashboardDetails = {
              INVESTED_AMOUNT: "",
              CURRENT_VALUE: "",
              XIRR_VALUE: "",
            };
            let totalInvestedAmount = 0;
            let totalCurrentValue = 0;
            if (Array.isArray(schemeDetails)) {
              schemeDetails.forEach((item: any) => {
                totalInvestedAmount += parseFloat(item.INVESTED_AMOUNT);
                totalCurrentValue += parseFloat(item.MARKET_VALUE);
              });
            }
            dashboardDetails.INVESTED_AMOUNT = String(totalInvestedAmount);
            dashboardDetails.CURRENT_VALUE = String(totalCurrentValue);
            dispatch(setMfDashboardDetails(dashboardDetails));
          }
        } else {
          updateLocalState({ type: "error", payload: Error("Empty List") });
        }
      }
    );
  }, [panNumber, folioDetails, isLoadingSchemeSummary]);

  const api_fetchFolioSummaryDetails = useCallback(async () => {
    if (isLoadingSummaryDetails) {
      return;
    }
    try {
      const apiBody = {
        FolioNumbers: folioDetails.map((item) => item.Folio_No).join(","),
        DeviceInfo: "Portal",
        XIRRFlag: "Y",
        IS_PRIMARY_HOLDING: loadFor === "primary" ? true : false,
      };
      const res = await fetchFolioSummaryDetails(apiBody);
      if (res?.data?.payload?.ProtfolioSummary?.XIRR) {
        let tempData = { ...mfDashboardDetail };
        tempData.XIRR_VALUE = String(res.data.payload.ProtfolioSummary.XIRR);
        dispatch(setMfDashboardDetails(tempData));
      }
    } catch (err) {
      console.error(err);
    }
  }, [mfDashboardDetail, folioDetails, isLoadingSummaryDetails]);

  useEffect(() => {
    if (!isEmpty(panNumber)) {
      checkAndinitApiFlow();
    }
  }, [panNumber, dob, folioDetails, folioSchemeDetails, mfDashboardDetail]);

  useEffect(() => {
    if (errInValidatePan || errInFolioSchemeSummary) {
      updateLocalState({
        type: "error",
        payload: (errInValidatePan ||
          errInFolioSchemeSummary ||
          Error("api failed")) as Error,
      });
    }
  }, [errInValidatePan, errInFolioSchemeSummary]);

  return localState;
};
